import React, { useState, useEffect } from 'react';
import { cmsApi } from '../../../lib/api/cms';

const SettingsEditor: React.FC = () => {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await cmsApi.getSettings();
      if (error) {
        setError('Seadete laadimine ebaõnnestus');
        return;
      }

      const settingsObj: Record<string, any> = {};
      data?.forEach(setting => {
        settingsObj[setting.key] = setting.value;
      });
      setSettings(settingsObj);
    } catch (err) {
      setError('Seadete laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      for (const [key, value] of Object.entries(settings)) {
        const { error } = await cmsApi.upsertSetting(key, value);
        if (error) {
          setError(`Seade "${key}" salvestamine ebaõnnestus`);
          return;
        }
      }
    } catch (err) {
      setError('Seadete salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Seadete haldus</h1>
        <p className="mt-2 text-gray-600">
          Halda veebilehe üldiseid seadeid ja metaandmeid.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* SEO Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">SEO seaded</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta pealkiri
              </label>
              <input
                type="text"
                value={settings.meta_title || ''}
                onChange={(e) => handleSettingChange('meta_title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Veebilehe meta pealkiri"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta kirjeldus
              </label>
              <textarea
                value={settings.meta_description || ''}
                onChange={(e) => handleSettingChange('meta_description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Veebilehe meta kirjeldus"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Märksõnad
              </label>
              <input
                type="text"
                value={settings.meta_keywords || ''}
                onChange={(e) => handleSettingChange('meta_keywords', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Märksõnad komadega eraldatud"
              />
            </div>
          </div>
        </div>

        {/* Company Settings */}
        {/* <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Ettevõtte seaded</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ettevõtte nimi
                </label>
                <input
                  type="text"
                  value={settings.company_name || ''}
                  onChange={(e) => handleSettingChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Forestry OÜ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registrikood
                </label>
                <input
                  type="text"
                  value={settings.company_reg_code || ''}
                  onChange={(e) => handleSettingChange('company_reg_code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="12345678"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aadress
              </label>
              <input
                type="text"
                value={settings.company_address || ''}
                onChange={(e) => handleSettingChange('company_address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Osmussaare tänav 8, 13811 Tallinn"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <input
                  type="text"
                  value={settings.company_phone || ''}
                  onChange={(e) => handleSettingChange('company_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="+372 6 707 110"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-post
                </label>
                <input
                  type="email"
                  value={settings.company_email || ''}
                  onChange={(e) => handleSettingChange('company_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="info@forestryest.ee"
                />
              </div>
            </div>
          </div>
        </div>*/}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvestamine...
              </>
            ) : (
              'Salvesta kõik seaded'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsEditor;